import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import TurnstileWidget from "@/components/TurnstileWidget";
import api from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1634822929277-0c51ca0e8846?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwaW50ZXJpb3IlMjBsaXZpbmclMjByb29tJTIwd2FybSUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4MjY5MjMyOXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Turnstile
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileExpired, setTurnstileExpired] = useState(false);

  useEffect(() => {
    api.get("/site-config").then(({ data }) => {
      setTurnstileEnabled(data.turnstile_enabled || false);
      setTurnstileSiteKey(data.turnstile_site_key || "");
    }).catch(() => {});
  }, []);

  const handleTurnstileToken = useCallback((token) => {
    setTurnstileToken(token);
    setTurnstileExpired(false);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
    setTurnstileExpired(true);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (turnstileEnabled && !turnstileToken) {
      setError("Veuillez compléter la vérification anti-robot.");
      return;
    }
    setError("");
    setLoading(true);
    const res = await login(email, password, remember, turnstileToken);
    setLoading(false);
    if (res.ok) navigate((res.user?.role === "admin" || res.user?.role === "superadmin") ? "/admin" : "/compte");
    else if (res.blocked) {
      navigate("/compte-bloque", {
        state: {
          support_token: res.support_token,
          user_email: res.user_email,
          user_phone: res.user_phone,
          user_name: res.user_name,
        },
      });
    } else setError(res.error);
  };

  return (
    <div className="grid lg:grid-cols-2 min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-5rem)]">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Intérieur" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="absolute bottom-12 left-12">
          <p className="font-serif text-4xl text-cream leading-tight">La lumière,<br />sublimée.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <Link to="/" data-testid="back-to-home" className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-ash hover:text-ink transition-colors mb-6">
            <ChevronLeft className="h-4 w-4" /> Accueil
          </Link>
          <Link to="/" aria-label="Déstockage Luminaires — accueil" className="inline-flex"><Logo to={null} size="md" variant="dark" testId="login-logo" /></Link>
          <h1 className="font-serif text-4xl text-ink tracking-tight mt-8">Se connecter</h1>
          <p className="text-graphite mt-2 text-sm">Accédez à vos commandes et informations.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-ash">Email</label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 h-12 rounded-sm border-stone bg-white" placeholder="vous@email.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-ash">Mot de passe</label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-2 h-12 rounded-sm border-stone bg-white" placeholder="••••••••" />
              <div className="mt-2 text-right">
                <Link to="/mot-de-passe-oublie" data-testid="login-forgot-password-link" className="text-xs text-graphite hover:text-ink underline underline-offset-2">
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-graphite cursor-pointer select-none">
              <input
                data-testid="login-remember-checkbox" type="checkbox" checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded-sm border-stone accent-ink"
              />
              Se souvenir de moi pendant 30 jours
            </label>

            {turnstileEnabled && turnstileSiteKey && (
              <div className="space-y-1">
                <TurnstileWidget
                  siteKey={turnstileSiteKey}
                  onToken={handleTurnstileToken}
                  onExpire={handleTurnstileExpire}
                  onError={handleTurnstileError}
                />
                {turnstileExpired && (
                  <p className="text-xs text-amber-600 text-center">Le défi a expiré, veuillez le recompléter.</p>
                )}
              </div>
            )}

            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading || (turnstileEnabled && !turnstileToken)}
              className="w-full bg-ink text-cream py-4 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          <p className="text-sm text-graphite mt-6">
            Pas encore de compte ?{" "}
            <Link to="/inscription" data-testid="goto-register" className="text-ink underline underline-offset-2">Créer un compte</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
